/* CROSS-SELL usage tracking for every complement surface.
 *
 * Owner, 2026-09-23: "make sure we can track usage on all these new things to see if they are being
 * used." Six surfaces shipped that day and none of them recorded anything — the same gap FBT had,
 * where the only evidence the feature existed was a discount code turning up on orders. Order data
 * answers "what did it earn"; it is silent on the much larger group who saw one and scrolled past.
 *
 * ⚠️ IMPRESSIONS ARE INTERSECTION-GATED, NOT RENDER-GATED. Most of these blocks sit below the fold
 * (the PDP one is ~1,500px down, the cart drawer's is under the line items). Counting a render as a
 * view would inflate every denominator and make each surface look worse than it is. A 'seen' is
 * only sent once the block is actually on screen.
 *
 * ONE 'seen' PER BLOCK PER PAGE VIEW, deduped in `sent` — the observer can fire repeatedly as the
 * customer scrolls back and forth, and each of those is the same impression.
 *
 * ⛔ FAIL SILENT, ALWAYS. Every call is wrapped and the endpoint answers 204 regardless. A shopper
 * adding a booster pad must never be blocked by an analytics write. The tracker also never touches
 * the click it observes — no preventDefault, no stopPropagation — so an Add still behaves exactly
 * as it would with this file absent.
 *
 * HOW A SURFACE OPTS IN: put `data-xsell-surface="<name>"` on the block, and optionally
 * `data-xsell-anchor` on it. The name must also exist in SURFACES in the worker's xselllog.js —
 * unknown names are dropped there rather than stored, so the two lists move together.
 *
 * ══ GENERIC FEATURE TRACKING — everything that is NOT a product recommendation ══════════════════
 * Owner, 2026-10-16: "I want tracking on all of them so we can see what is being used and what
 * isn't." He had asked the same on 2026-09-23 and got a cross-sell-only answer. Measured that day:
 * 93 custom feature files in the theme, 6 with any instrumentation, all 6 cross-sell only. Quick
 * Reorder, Buy Again, size charts, the autoship portal, the wishlist, the finder — all dark.
 *
 * So one attribute instruments anything, with NO new JavaScript per feature:
 *     data-llm-track="quick-reorder:open"      fires on click
 *     data-llm-track-seen="health-goals"       fires once when it scrolls into view
 *     data-llm-track-detail="overnight"        optional free text on either
 * The handler is delegated on document, so it covers markup injected long after load — the drawers
 * and modals that defeated the cross-sell tracker for a month.
 *
 * ⚠️ feature:action — if the colon is omitted the action defaults to 'click'. Names are normalised
 * worker-side (lowercase, [a-z0-9-]) and the vocabulary is OPEN, unlike xsell's closed SURFACES
 * list, so a new feature is measurable the day it ships. The cost is that a typo becomes its own
 * row rather than being rejected; schema/feature_log.sql carries the near-duplicate report for it.
 */
(function () {
  var ENDPOINT = 'https://llmedico-meilisearch-sync.llmedico.workers.dev/xsell-log';
  var sent = new WeakSet();

  /* ⛔⛔ ONE TRANSPORT: fetch + keepalive. NEVER navigator.sendBeacon. NEVER AGAIN.
   *
   * Owner, 2026-10-16: "I just want to be able to track usage of all these elements we are adding
   * to the site." Beacon cannot give that, because when it fails it fails SILENTLY and there is no
   * way to find out — sendBeacon returns true meaning "queued", never "delivered".
   *
   * The evidence against it, all measured on this store:
   *   · 3 weeks recorded 31,965 impressions and ZERO clicks. A `|f` marker settled it 2026-10-14:
   *       2026-10-13   32 adds — 32 via fetch, 0 via beacon
   *       2026-10-14   61 adds — 61 via fetch, 0 via beacon
   *     Beacon never delivered a single CLICK. The surfaces had been converting all along
   *     (pdp 1.26%, oos 1.65%, similar 2.27%); the instrument was the bug.
   *   · 2026-10-16, identical payloads from the live /cart page, same tab, same second:
   *       fetch      -> 204, row in D1
   *       sendBeacon -> returned true, ROW NEVER ARRIVED
   *     So it drops impressions too, for at least some clients, while appearing to succeed.
   *
   * Beacon does deliver for most real traffic (18k+ impressions), which is exactly what makes it
   * dangerous: the loss is partial, silent and unquantifiable. We cannot know what share of usage
   * is missing, and "we think it's roughly right" is not tracking.
   *
   * ⚠️ THERE IS NO COST TO THIS. I first described the choice to the owner as reliability vs request
   * volume; that was wrong. post() sends ONE request per call either way, and impressions are
   * already batched one-per-block, not one-per-row. Identical volume, strictly more reliable, and
   * fetch yields a status code so a failure can actually be seen.
   *
   * ⚠️ keepalive is the part that matters: the most interesting event — "Add", in the cart drawer —
   * is usually followed immediately by a navigation to checkout, and keepalive is precisely the
   * guarantee that an in-flight request survives it. That is the job sendBeacon used to do, done by
   * an API that reports what happened.
   *
   * ⚠️ Historical rows whose `page` carries a '|f' suffix are real adds, not duplicates — the beacon
   * copy never arrived, so nothing was ever double-counted. Strip it when querying old rows:
   *     SELECT replace(page,'|f','') … */
  function post(payload) {
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () {});
    } catch (e) { /* analytics must never throw into the page */ }
  }

  function rowsOf(block) {
    return [].slice.call(block.querySelectorAll('[data-xsell-product]'));
  }

  /* Returns true only if an impression was actually logged, so the caller knows whether it may
     stop watching. ⚠️ The early returns below must NOT mark the block as sent — see the note on
     observe().  */
  function logSeen(block) {
    if (sent.has(block)) return true;
    var surface = block.getAttribute('data-xsell-surface');
    var anchor = block.getAttribute('data-xsell-anchor') || location.pathname;
    var rows = rowsOf(block);
    if (!surface || !rows.length) return false;
    sent.add(block);
    // Batched: one impression costs one request however many rows it held.
    post({
      page: location.pathname,
      events: rows.map(function (r) {
        return { event: 'seen', surface: surface, product: r.getAttribute('data-xsell-product'), anchor: anchor };
      }),
    });
    return true;
  }

  /* ⚠️ 0.4 WAS TOO STRICT FOR THE DRAWERS, and it made three surfaces unmeasurable.
   *
   * Measured 2026-10-14: every surface whose impressions work (pdp, oos, similar) is SERVER-RENDERED
   * into the page. Every surface whose impressions were missing (atc, drawer, autoship) is INJECTED
   * into a scrollable drawer — and those three recorded MORE adds than impressions, which is
   * impossible unless the impression bar is wrong, since you cannot tap a row you never saw.
   *
   * IntersectionObserver accounts for clipping ANCESTORS, so a block sitting below the fold inside a
   * drawer body never reaches 40% of its own height on screen however long the customer looks at the
   * top of it. A tall three-row block in a short drawer can top out well under that.
   *
   * So: count an impression when a MEANINGFUL part is on screen — a tenth of the block, or 120px of
   * it, whichever comes first. The extra thresholds matter as much as the numbers: with a single
   * 0.4 entry the callback never runs at lower ratios, so there is nothing to evaluate.
   *
   * This is inferred from the rendered-vs-injected split rather than from a reproduction, so treat
   * the next few days of `seen` counts on atc/drawer/autoship as the real test. If they are still
   * near zero, the cause is upstream of the threshold — look at whether the block is observed at all
   * while its drawer is closed. */
  var io = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var r = en.intersectionRect;
          var enough = en.intersectionRatio >= 0.1 || (r && r.height >= 120);
          /* ⚠️ Only stop watching once something was ACTUALLY logged. Unobserving regardless was
             half of the bug below: a block seen while still empty was dropped AND forgotten. */
          if (enough && logSeen(en.target)) io.unobserve(en.target);
        });
      }, { threshold: [0, 0.1, 0.25, 0.4] })
    : null;

  /* ⛔⛔ DO NOT WATCH AN EMPTY BLOCK. This is the whole bug behind "impressions broken on
     atc / drawer / autoship / reorder / home", and the earlier diagnosis was wrong twice over.
     Measured in D1 on 2026-10-16, the four days around the previous attempt:

                  10-13    10-14    10-15    10-16      (seen / adds)
         pdp     1233/14  1680/22  1770/23  1029/29     works
         atc        0/9     12/25     0/16      0/5     broken
         drawer     0/4      6/11     0/7      0/6      broken

     Those surfaces record MORE ADDS THAN IMPRESSIONS, which is impossible — nobody taps a row they
     never saw. So the instrument was wrong, not the surface. (The 10-14 figures are almost
     certainly my own testing that day, not recovery: the threshold change shipped on 10-14 and
     10-15 went straight back to zero.)

     ⚠️ THE "SERVER-RENDERED vs INJECTED" THEORY IN THE OLD COMMENT WAS FALSE. Not one surface
     emits `data-xsell-product` in Liquid — checked all six. Every surface injects its rows. The
     real difference is WHEN the block is first on screen:
       · pdp / oos / similar sit BELOW THE FOLD, so the observer fires on scroll, seconds after the
         rows have been injected. They log fine.
       · atc / drawer / autoship appear INSTANTLY when their drawer opens, so the observer fires on
         an empty shell — and logSeen() used to mark the block `sent` BEFORE checking `rows.length`,
         then return. The block was burned: `sent` blocked logSeen, and this function skipped it
         forever after.

     So empty blocks are not observed at all. The MutationObserver that watches for injected
     drawers brings us straight back here when the rows land, and the block is picked up then. */
  function observe(root) {
    var blocks = [].slice.call((root || document).querySelectorAll('[data-xsell-surface]'));
    blocks.forEach(function (b) {
      if (sent.has(b)) return;
      if (!rowsOf(b).length) return;
      // No IntersectionObserver (old Safari): fall back to counting the render. Over-counts
      // impressions slightly for a small share of traffic, which beats losing them entirely.
      if (io) io.observe(b); else logSeen(b);
    });
  }

  document.addEventListener('DOMContentLoaded', function () { observe(document); });
  observe(document);

  /* The cart drawer, the add-to-cart confirmation and Quick Reorder all inject their blocks AFTER
     load, so a one-shot scan at DOMContentLoaded would miss every one of them. */
  if ('MutationObserver' in window) {
    new MutationObserver(function () { observe(document); })
      .observe(document.documentElement, { childList: true, subtree: true });
  }

  /* Delegated on the document so it covers blocks that did not exist when this ran. */
  document.addEventListener('click', function (evt) {
    try {
      var row = evt.target && evt.target.closest && evt.target.closest('[data-xsell-product]');
      if (!row) return;
      var btn = evt.target.closest('button, a');
      if (!btn || !row.contains(btn)) return;
      var block = row.closest('[data-xsell-surface]');
      if (!block) return;
      /* No second argument any more — post() has ONE transport. The old `durable` flag selected
         between beacon and fetch; impressions and clicks now take the same path. */
      post({
        page: location.pathname,
        events: [{
          event: 'add',
          surface: block.getAttribute('data-xsell-surface'),
          product: row.getAttribute('data-xsell-product'),
          anchor: block.getAttribute('data-xsell-anchor') || location.pathname,
        }],
      });
    } catch (e) { /* never interfere with the click itself */ }
  }, true);

  /* ══ GENERIC FEATURE TRACKING ═══════════════════════════════════════════════════════════════
     Everything that is not a product recommendation. See the header for the attribute contract.
     Separate endpoint and table because the shape differs — feature/action, not product/anchor —
     and mixing them would corrupt the cross-sell conversion queries. */
  var FEATURE_ENDPOINT = ENDPOINT.replace('/xsell-log', '/feature-log');

  function postFeature(feature, action, detail) {
    if (!feature) return;
    try {
      fetch(FEATURE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: location.pathname,
          events: [{ feature: feature, action: action || 'click', detail: detail || '' }],
        }),
        keepalive: true,
      }).catch(function () {});
    } catch (e) { /* analytics must never throw into the page */ }
  }

  /* Delegated + capture, same as the cross-sell handler: it must see clicks on elements that did
     not exist when this ran, and must not depend on the feature's own handlers running first. */
  document.addEventListener('click', function (evt) {
    try {
      var el = evt.target && evt.target.closest && evt.target.closest('[data-llm-track]');
      if (!el) return;
      var spec = el.getAttribute('data-llm-track') || '';
      var bits = spec.split(':');
      postFeature(bits[0], bits[1], el.getAttribute('data-llm-track-detail'));
    } catch (e) { /* never interfere with the click itself */ }
  }, true);

  /* Impressions for features that are blocks rather than buttons — "was it even on screen?" is the
     difference between a feature nobody wants and one nobody can find. Reuses the same
     intersection discipline as the cross-sell blocks rather than counting renders. */
  var seenFeature = new WeakSet();
  var fio = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting || seenFeature.has(en.target)) return;
          var r = en.intersectionRect;
          if (!(en.intersectionRatio >= 0.1 || (r && r.height >= 120))) return;
          seenFeature.add(en.target);
          fio.unobserve(en.target);
          postFeature(en.target.getAttribute('data-llm-track-seen'), 'seen',
            en.target.getAttribute('data-llm-track-detail'));
        });
      }, { threshold: [0, 0.1, 0.25, 0.4] })
    : null;

  function observeFeatures() {
    var els = [].slice.call(document.querySelectorAll('[data-llm-track-seen]'));
    els.forEach(function (el) {
      if (seenFeature.has(el)) return;
      /* ⚠️ A zero-height element is a CLOSED drawer or modal, not an impression. Observing it is
         correct — it fires when it opens — but counting it now would be a lie. */
      if (fio) fio.observe(el);
      else { seenFeature.add(el); postFeature(el.getAttribute('data-llm-track-seen'), 'seen'); }
    });
  }
  document.addEventListener('DOMContentLoaded', observeFeatures);
  observeFeatures();
  if ('MutationObserver' in window) {
    new MutationObserver(observeFeatures)
      .observe(document.documentElement, { childList: true, subtree: true });
  }

  /* Exposed so a feature with no natural DOM hook — something that happens in JS, like an autoship
     skip confirming — can report itself: window.llmTrack('autoship-skip','complete'). */
  window.llmTrack = postFeature;
})();
